/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { useState, useEffect } from "react";
import { ChromePicker as Chrome } from "react-color";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import { cn } from "tailwind-variants";
import { getContrastingColor } from "./utils";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { renderList } from "common/vue/render-list";
import "./ColorPicker.scss";

export default function ColorPicker({
  colors,
  value,
  showColorLabel = false,
  variant = null,
  size = "large",
  modalPosition = undefined,
  onChange,
  slots,
  ...restProps
}: any) {
  const [color, setColor] = useState(value || colors[0]);
  const [isOpened, setIsOpened] = useState(false);
  const [lastTrackedColor, setLastTrackedColor] = useState(null);

  function isInline() {
    return variant === "inline";
  }

  function prepareColors() {
    return colors.map((colorOption: any) => {
      if (typeof colorOption === "object") return colorOption;
      return {
        value: colorOption,
        name: colorOption
      };
    });
  }

  function colorValues() {
    return prepareColors().map((colorOption: any) => colorOption.value);
  }

  function selectedColorValue() {
    return typeof color === "string" ? color : color?.hex || null;
  }

  function isCustom() {
    return selectedColorValue() !== null && !colorValues().includes(selectedColorValue());
  }

  function disableApply() {
    return !color?.hex;
  }

  function handleClose() {
    if (isInline()) return;
    setIsOpened(false);
  }

  function trackColorSelection(colorValue: any, trigger: any) {
    if (!colorValue || lastTrackedColor === colorValue) {
      return;
    }
    const matchedColor = prepareColors().find((option: any) => option.value === colorValue);
    submitDtfEvent("dtf_image_placement_color_selected", {
      trigger,
      variant: variant || null,
      size,
      show_color_label: showColorLabel,
      color_value: colorValue,
      color_name: matchedColor?.name || null,
      is_custom_color: !colorValues().includes(colorValue)
    });
    setLastTrackedColor(colorValue);
  }

  function handleSelect(selectedValue: any) {
    if (isInline()) {
      setColor(
        typeof selectedValue === "string"
          ? {
              hex: selectedValue
            }
          : selectedValue
      );
      return;
    }
    onChange?.(selectedValue);
    trackColorSelection(selectedValue, "swatch");
    handleClose();
  }

  function handleOpen() {
    if (isInline()) return;
    setIsOpened(true);
  }

  function handleApply() {
    handleSelect(color.hex);
  }

  function isCustomSelected(checkValue: any) {
    return isCustom() && selectedColorValue() === checkValue;
  }

  useEffect(() => {
    if (value !== undefined && value !== null) {
      setColor(value);
    }
  }, [value]);

  useEffect(() => {
    const newValue = typeof color === "string" ? color : color?.hex;
    if (isInline()) {
      onChange?.(newValue);
      trackColorSelection(newValue, "inline_picker");
    }
  }, [color]);

  return (
    <div
      {...restProps}
      className={cn(restProps?.className, {
        "color-picker": true,
        "color-picker--inline": isInline(),
        "color-picker--with-names": showColorLabel
      })}
    >
      <div className="color-picker__items">
        {renderList(prepareColors(), colorOption => (
          <button
            className={cn(
              `color-picker__item color-picker__item--${size}`,
              colorOption.value === selectedColorValue() && "color-picker__item--selected"
            )}
            key={colorOption.value}
            type="button"
            style={{
              backgroundColor: colorOption.value
            }}
            onClick={e => {
              e.preventDefault();
              handleSelect(colorOption.value);
            }}
            data-testid="color-picker-button"
          >
            {showColorLabel ? (
              <div className="color-picker__color-name">{colorOption.name}</div>
            ) : null}
            {colorOption.value === selectedColorValue() ? (
              <UseSvg
                className="color-picker__checkbox"
                icon="checkbox"
                style={getContrastingColor(colorOption.value)}
                data-testid="color-picker-checkbox"
              />
            ) : null}
          </button>
        ))}
        <button
          className={cn(
            `color-picker__item color-picker__item--custom color-picker__item--${size}`,
            isCustomSelected(value) && "color-picker__item--selected"
          )}
          type="button"
          onClick={e => {
            e.preventDefault();
            handleOpen();
          }}
          data-testid="color-picker-button-1"
        >
          <UseSvg
            className="color-picker__icon"
            icon="custom-color"
            data-testid="color-picker-icon"
          />

          {isCustomSelected(value) ? (
            <UseSvg
              className="color-picker__checkbox"
              icon="checkbox"
              data-testid="color-picker-checkbox-1"
            />
          ) : null}
        </button>
      </div>
      {isInline() ? (
        <Chrome
          className="color-picker__selector"
          color={typeof color === "string" ? color : color?.hex}
          onChange={val => {
            setColor(val);
          }}
          disableAlpha
          data-testid="color-picker-selector"
        />
      ) : isOpened ? (
        <div
          className={cn(
            "color-picker__modal",
            modalPosition && `color-picker__modal--${modalPosition}`
          )}
        >
          <div
            className="color-picker__backdrop"
            onClick={e => {
              e.preventDefault();
              handleClose();
            }}
            data-testid="color-picker-backdrop"
          />

          <div className="color-picker__content">
            <div className="color-picker__header">
              {" "}
              Select custom color{" "}
              <UseSvg
                className="color-picker__close"
                icon="menu-close"
                onClick={(e: any) => {
                  e.preventDefault();
                  handleClose();
                }}
                data-testid="color-picker-close"
              />
            </div>
            {slots?.["picker-preview"]?.()}
            <Chrome
              className="color-picker__selector"
              color={typeof color === "string" ? color : color?.hex}
              onChange={val => {
                setColor(val);
              }}
              disableAlpha
              data-testid="color-picker-selector-1"
            />

            <div className="color-picker__actions">
              <button
                className="color-picker__cancel"
                type="button"
                onClick={e => {
                  e.preventDefault();
                  handleClose();
                }}
                data-testid="color-picker-cancel"
              >
                {" "}
                Cancel{" "}
              </button>
              <button
                className={cn(
                  "color-picker__apply",
                  disableApply() && "color-picker__apply--disabled"
                )}
                type="button"
                disabled={disableApply()}
                onClick={e => {
                  e.preventDefault();
                  handleApply();
                }}
                data-testid="color-picker-button-2"
              >
                {" "}
                Apply color{" "}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
